// Tap spots: things in a scene that answer a tap (issue #129). A scene exports `spots`, a few boxes in scene pixels
// with a kind each, { kind, x, y, w, h } (docs/SCENE_GUIDE.md §2). A tap on one plays its kind's sound on the track's
// next beat, in its key (player.tap, site/js/audio/taps.js), and shows its kind's answer right there, on the page's own
// clock: the spot lights up at once, brightest on the beat, where the rest plays (sparkles, a ring, pigeons flying
// off…), then all of it fades. A visitor who prefers reduced motion gets the light alone. A spot only answers:
// nothing hints at it by moving. Drawn in the page's overlay buffers, like the reactions (site/js/live/reactions.js):
// never in a scene, so PNG, MP4 and the mini player never show it. A tap anywhere else still drops the picked reaction
// (site/js/app.js). Hit-testing and poses are pure (tests/spots.test.mjs).
// A spot of a layer kind (issue #38) is a switch instead: a thing that adds a part to the music while it is on
// (site/js/audio/layers.js, site/js/ui/layers.js). It carries the thing's `name` too, and shows whether it is on with
// a steady light over the box and a small note above it (drawLayers): nothing about it moves once it is on.
import { TAU, clamp, smooth, hash, hex, bayer, makeCanvas, makeHalo, drawHalo } from '../engine/core.js';

export const TOUCH_TARGET = 44;       // CSS px: under a finger, a smaller box counts as this wide and tall
export const ROWS_OFF = 63;           // the scene's bottom rows that the dock and a wide window can cover: none of a spot is there

// kind → sound: a taps.js voice and its `notes` (semitones over the tonic at or above MIDI `from`, each moved onto the
// track's scale, `gap` s apart, ringing off with τ `decay`); glow: the light, [colour, strength] over the box, or
// [colour, strength, radius] round the tapped point (a wide box: the water); show: what plays from the beat (SHOWS);
// life: seconds from the beat to the end. Another scene's spots need no code: a box and one of these kinds.
export const KINDS = {
  landmark: { sound: { voice: 'glass', from: 72, notes: [0, 4, 7, 12], gap: 0.09, decay: 0.2 }, glow: ['#ffe9b8', 0.5], show: 'sparkle', life: 1.6 },
  window: { sound: { voice: 'glass', from: 72, notes: [7, 12], gap: 0.07, decay: 0.16 }, glow: ['#ffd27a', 0.5], show: 'light', life: 1.4 },
  neon: { sound: { voice: 'buzz', from: 48, notes: [0] }, glow: ['#ffffff', 0.35], show: 'flicker', life: 1.2 },
  cat: { sound: { voice: 'mew', from: 65, notes: [0, 5] }, glow: ['#ffc9a0', 0.3], show: 'heart', life: 1.4 },
  birds: { sound: { voice: 'flutter', from: 60, notes: [7, 4] }, glow: ['#cfd6ea', 0.3], show: 'scatter', life: 1.6 },
  bell: { sound: { voice: 'bell', from: 55, notes: [0], decay: 0.3 }, glow: ['#ffe6a6', 0.5], show: 'ring', life: 1.5 },
  water: { sound: { voice: 'splash', from: 84, notes: [0, 7] }, glow: ['#bfe3ff', 0.5, 10], show: 'ripple', life: 1.5 },
  // not a spot's to pick: how one of a scene's hidden things answers (site/js/ui/secrets.js gives each this kind)
  secret: { sound: { voice: 'glass', from: 76, notes: [0, 7, 12], gap: 0.1, decay: 0.24 }, glow: ['#fff3c4', 0.45], show: 'find', life: 1.6 },
  // the layer kinds: `layer` is the part the thing switches (a layers.js LAYER_IDS id), `does` what it adds, in words
  shaker: { layer: 'shaker', does: 'a shaker', glow: ['#ffcf8a', 0.3] },
  pad: { layer: 'pad', does: 'a warm pad', glow: ['#fff0c0', 0.3] },
  twinkle: { layer: 'twinkle', does: 'twinkling notes', glow: ['#ffe2b0', 0.22] },
};
export const SECRET = 'secret';
/** the part a spot switches (a layers.js id), or null: a spot that answers a tap once */
export const layerOf = spot => KINDS[spot?.kind]?.layer ?? null;

/* ---------------- where a tap lands ---------------- */
const known = s => !!s && s.kind !== SECRET && Object.hasOwn(KINDS, s.kind) && [s.x, s.y, s.w, s.h].every(Number.isFinite) && s.w > 0 && s.h > 0;
const kept = new WeakMap();
/** The spots of `scene` this page can answer (a tab left open across a deploy may meet a kind it doesn't know). */
export function spotsOf(scene) {
  if (!scene || !Array.isArray(scene.spots)) return [];
  if (!kept.has(scene)) kept.set(scene, scene.spots.filter(known));
  return kept.get(scene);
}
/** The spots of `scene` that switch a layer, one per layer (the first in the list), each with a name. */
export const layerSpots = scene => spotsOf(scene).filter((s, i, all) => layerOf(s) && typeof s.name === 'string' && s.name
  && all.findIndex(q => layerOf(q) === layerOf(s)) === i);

/** Half a finger's target in scene pixels where the page shows the scene as `view` (display.view); 0 for a mouse. */
export const padOf = (view, touch) => (touch ? TOUCH_TARGET / 2 * view.w / view.width : 0);

/**
 * The spot at scene pixel (x, y), or null. `pad` (padOf) widens the boxes smaller than a finger's target to it and no
 * further, so a tap away from every spot stays a reaction. A box the pixel is in comes before one it only reaches
 * through that padding; among boxes it is in, the smaller one, then the one whose centre is nearer; among padded
 * ones, the nearer box, then the smaller; then the earlier in the list.
 */
export function hitSpot(spots, x, y, pad = 0) {
  let best = null, rank = null;
  spots.forEach((s, i) => {
    const dx = Math.abs(x + 0.5 - (s.x + s.w / 2)), dy = Math.abs(y + 0.5 - (s.y + s.h / 2));
    if (dx > Math.max(s.w / 2, pad) || dy > Math.max(s.h / 2, pad)) return;
    const off = Math.hypot(Math.max(0, dx - s.w / 2), Math.max(0, dy - s.h / 2));   // how far outside the box itself
    const r = off > 0 ? [1, off, s.w * s.h, i] : [0, s.w * s.h, Math.hypot(dx, dy), i];
    if (!rank || first(r, rank)) { best = s; rank = r; }
  });
  return best;
}
/** whether rank a comes before rank b: the first number they differ in is smaller */
function first(a, b) {
  for (let k = 0; k < a.length; k++) if (a[k] !== b[k]) return a[k] < b[k];
  return false;
}

/* ---------------- answers ---------------- */
const endOf = a => a.hit + a.life * 1000;
/** Whether `spot` is still answering at `now` (ms): another tap on it does nothing until it is done. */
export const answering = (list, spot, now) => list.some(a => a.spot === spot && now < endOf(a));

/**
 * A tap on `spot` at scene pixel `at` ({ x, y }), `now` (ms on the page's clock): adds its answer to `list` and
 * returns it, or null while the spot is still answering. `hit` (ms): when its sound lands, the beat the show plays on
 * (left out: at once); `still` (prefers-reduced-motion): the spot lights up and fades, and nothing moves.
 */
export function answerSpot(list, spot, at, now, { hit = now, still = false } = {}) {
  if (answering(list, spot, now)) return null;
  const a = {
    spot, x: clamp(Math.round(at.x), spot.x, spot.x + spot.w - 1), y: clamp(Math.round(at.y), spot.y, spot.y + spot.h - 1),
    born: now, hit: Math.max(now, hit), life: KINDS[spot.kind].life, still, seed: Math.floor(now) | 0,
  };
  list.push(a);
  return a;
}

/**
 * An answer at `now` (ms): { glow: its light, 0..1; t: seconds since the beat; u: how far its show is, 0..1 }, with
 * t and u -1 before the beat and for a still one (no show); null once it is over. The light is on from the tap, a
 * third bright, grows to the beat and fades from there.
 */
export function answerPose(a, now) {
  const t = (now - a.hit) / 1000;
  if (now < a.born || t >= a.life) return null;
  const glow = t < 0 ? 0.35 + 0.65 * smooth(0, 1, (now - a.born) / (a.hit - a.born)) : 1 - smooth(0.1 * a.life, a.life, t);
  return t < 0 || a.still ? { glow, t: -1, u: -1 } : { glow, t, u: t / a.life };
}

/* ---------------- drawing (scene pixels; `glow` is put on screen as light, `ink` on top of it) ---------------- */
const LEVELS = 6;
const lights = new Map();             // a box's light, by size and colour: drawn on first use
/** a dithered oval of light over a w x h box and a margin round it: { c, m } */
function lightOf(w, h, col) {
  const key = `${w}x${h}${col}`;
  if (!lights.has(key)) {
    const m = clamp(Math.round(Math.min(w, h) / 2) + 3, 5, 14), cw = w + 2 * m, ch = h + 2 * m, rgb = hex(col);
    const [c, x] = makeCanvas(cw, ch), img = x.createImageData(cw, ch), d = img.data;
    for (let j = 0; j < ch; j++) for (let i = 0; i < cw; i++) {
      const far = Math.hypot((i + 0.5) / (cw / 2) - 1, (j + 0.5) / (ch / 2) - 1);
      const q = far < 1 ? Math.floor((1 - far) ** 1.6 * LEVELS + bayer(i, j)) / LEVELS : 0;
      if (q <= 0) continue;
      const o = (j * cw + i) * 4;
      d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(Math.min(1, q) * 255);
    }
    x.putImageData(img, 0, 0);
    lights.set(key, { c, m });
  }
  return lights.get(key);
}
const rounds = new Map();             // the light round a point, by radius and colour
const roundOf = (r, col) => rounds.get(r + col) ?? rounds.set(r + col, makeHalo(r, col, 1, 1.6)).get(r + col);

const dot = (ink, x, y, col, a = 1) => { if (a > 0.02) { ink.globalAlpha = Math.min(1, a); ink.fillStyle = col; ink.fillRect(x, y, 1, 1); } };
/** an oval's outline, one pixel thick, each pixel once; rows above `top` are left out (a ripple stays on its water) */
function oval(ink, cx, cy, rx, ry, col, a, top = -Infinity) {
  const seen = new Set(), put = (x, y) => { if (y >= top && !seen.has(x * 512 + y)) { seen.add(x * 512 + y); dot(ink, x, y, col, a); } };
  for (let i = -rx; i <= rx; i++) { const j = Math.round(ry * Math.sqrt(1 - (i * i) / (rx * rx))); put(cx + i, cy - j); put(cx + i, cy + j); }
  for (let j = -ry; j <= ry; j++) { const i = Math.round(rx * Math.sqrt(1 - (j * j) / (ry * ry))); put(cx - i, cy + j); put(cx + i, cy + j); }
}
const WINGS = [                       // a bird's wings up, level and down
  ['ww...ww', '.ww.ww.', '..wbw..', '...b...'], ['.......', 'wwwbwww', '.wwbww.', '.......'], ['.......', '..wbw..', '.wwbww.', 'ww...ww'],
];
const HEART = ['.x.x.', 'xoxxx', 'xxxxx', '.xxx.', '..x..'];
/** rows of characters as pixels from (x, y), `cols` their colours, over a shade a row lower (it reads on a lit wall too) */
function sprite(ink, rows, x, y, cols, a) {
  for (const drop of [1, 0]) rows.forEach((row, j) => [...row].forEach((ch, k) => {
    if (ch !== '.') dot(ink, x + k, y + j + drop, drop ? '#100a22' : cols[ch], drop ? a * 0.55 : a);
  }));
}
const fill = (g, s, col, a) => { g.globalCompositeOperation = 'lighter'; g.globalAlpha = Math.min(1, a); g.fillStyle = col; g.fillRect(s.x, s.y, s.w, s.h); };

// What each kind shows: (glow, ink, answer, pose, colour). The first two light the box itself, beat or not; the rest
// play from the beat (pose.u >= 0).
const SHOWS = {
  /** a window: the light comes on behind the pane */
  light(glow, ink, a, p, col) { fill(glow, a.spot, col, p.glow * 0.4); },
  /** a neon sign: its tubes stutter as they catch, then burn brighter */
  flicker(glow, ink, a, p, col) {
    const on = p.t < 0 || p.t > 0.4 || hash(a.seed, Math.floor(p.t * 16)) > 0.45;
    fill(glow, a.spot, col, p.glow * (on ? 0.3 : 0.06));
  },
  /** a landmark: lights twinkle up and down it, more of them where a tower is wider, near its foot */
  sparkle(glow, ink, a, p) {
    if (p.u < 0) return;
    const s = a.spot, n = clamp(Math.round(s.h / 3), 8, 16), len = 0.22 / a.life;
    for (let i = 0; i < n; i++) {
      const from = hash(a.seed, i, 1) * (0.8 - len), on = (p.u - from) / len;
      if (on < 0 || on >= 1) continue;
      const v = hash(a.seed, i, 2), x = Math.round(s.x + s.w / 2 + (hash(a.seed, i, 3) * 2 - 1) * (0.2 + 0.8 * v) * (s.w / 2 - 1));
      const y = s.y + Math.floor(v * s.h), peak = Math.sin(Math.PI * on);
      dot(ink, x, y, '#ffffff', 0.4 + 0.6 * peak);
      if (i % 3 === 0 && peak > 0.7) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) dot(ink, x + dx, y + dy, '#fff3c4', 0.55);
    }
  },
  /** a bell: two rings widen from it */
  ring(glow, ink, a, p) {
    if (p.t < 0) return;
    const s = a.spot, cx = Math.round(s.x + s.w / 2), cy = Math.round(s.y + s.h / 2);
    for (let k = 0; k < 2; k++) {
      const u = (p.t - k * 0.25) / 1.1;
      if (u >= 0 && u < 1) { const r = Math.round(s.w / 2 + 14 * (1 - (1 - u) ** 2)); oval(ink, cx, cy, r, r, '#fff0c0', 0.9 * (1 - u) ** 1.2); }
    }
  },
  /** water: drops hop where it was touched and two ripples spread, flat on the surface */
  ripple(glow, ink, a, p) {
    if (p.t < 0) return;
    for (let k = 0; k < 2; k++) {
      const u = (p.t - k * 0.28) / 1.1;
      if (u >= 0 && u < 1) { const rx = Math.round(2 + 9 * u); oval(ink, a.x, a.y, rx, Math.max(1, Math.round(rx / 3.5)), '#d9efff', 0.7 * (1 - u), a.spot.y); }
    }
    const up = p.t / 0.45;
    if (up < 1) for (let i = -1; i <= 1; i++) dot(ink, a.x + i * 3, a.y - Math.round((3 + 2 * hash(a.seed, i + 1)) * Math.sin(Math.PI * up)), '#e8f6ff', 1 - up);
  },
  /** birds: three take off from the box, up and away, and are gone */
  scatter(glow, ink, a, p) {
    if (p.u < 0) return;
    const s = a.spot, away = 1 - (1 - p.u) ** 2, fade = Math.min(1, p.t / 0.08) * (1 - smooth(0.6, 1, p.u));
    for (let i = 0; i < 3; i++) {
      const dir = -TAU / 4 + (i - 1) * 0.7 + (hash(a.seed, i, 4) - 0.5) * 0.5, far = (14 + 6 * hash(a.seed, i, 5)) * a.life * away;
      const x = Math.round(s.x + (i + 0.5) * s.w / 3 + Math.cos(dir) * far) - 3, y = Math.round(s.y + s.h / 2 + Math.sin(dir) * far - 4 * p.u) - 2;
      const beat = [0, 1, 2, 1][Math.floor(p.t / 0.08 + i) % 4];
      sprite(ink, WINGS[beat], x, y, { w: '#dfe3f0', b: '#8d93ab' }, fade);
    }
  },
  /** a cat: a small heart rises from where it was petted */
  heart(glow, ink, a, p) {
    if (p.u < 0) return;
    const x = a.x - 2 + Math.round(Math.sin(p.t * 5)), y = a.spot.y - 5 - Math.round(8 * (1 - (1 - p.u) ** 1.6));
    const fade = smooth(0, 0.12, p.u) * (1 - smooth(0.6, 1, p.u));
    sprite(ink, HEART, x, y, { x: '#ff8fa6', o: '#ffe0e6' }, fade);
  },
  /** a hidden thing: a small star opens over it and closes, two motes drift up beside it */
  find(glow, ink, a, p) {
    if (p.u < 0) return;
    const s = a.spot, cx = Math.round(s.x + s.w / 2), cy = s.y - 4, fade = 1 - smooth(0.45, 1, p.u);
    const open = Math.sin(Math.PI * Math.min(1, p.u / 0.6)), arm = open > 0.75 ? 2 : open > 0.3 ? 1 : 0;
    dot(ink, cx, cy, '#ffffff', fade);
    for (let k = 1; k <= arm; k++) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) dot(ink, cx + dx * k, cy + dy * k, '#fff3c4', fade * (k === 1 ? 0.85 : 0.5));
    for (const side of [-1, 1]) dot(ink, cx + side * 4 + Math.round(Math.sin(p.t * 4 + side)), cy + 3 - Math.round(7 * p.u) + side, '#ffe9b8', 0.7 * fade);
  },
};

/* ---------------- layers: a spot that is a switch ---------------- */
/**
 * How a layer's spot looks at `now` (ms on the page's clock), from its mark { want: switched on, on: sounding, since:
 * when it was switched (ms), hit: when the bar line it changes on is heard (ms; null: not known, the music isn't
 * playing), still (prefers-reduced-motion) }: { glow: its light, 0..1, wait: it hasn't changed yet }, or null when it is
 * off with nothing waiting. On: a steady light. Waiting to come on, the light is there from the tap, a third bright,
 * and grows to the bar line; waiting to go off, it sinks towards it. With `still`, or no bar line to count to, a
 * waiting light is half bright and doesn't change.
 */
export function layerPose(m, now) {
  if (!m.want && !m.on) return null;
  if (m.want === m.on) return { glow: 1, wait: false };
  const u = m.still || m.hit == null || m.hit <= m.since ? null : smooth(0, 1, clamp((now - m.since) / (m.hit - m.since), 0, 1));
  return { glow: u === null ? 0.5 : m.want ? 0.35 + 0.5 * u : 0.85 - 0.5 * u, wait: true };
}
const NOTE = ['..xxx', '..x.x', '..x..', '..x..', 'xxx..', 'xxx..'];   // an eighth note
/** Draws the layers' marks ([{ spot, want, on, since, hit, still }]) at `now` into `glow` and `ink`: the light over
 *  each box and a note above it, pale while it waits for its bar line. Returns how many it drew. */
export function drawLayers(glow, ink, marks, now) {
  let drawn = 0;
  for (const m of marks) {
    const p = layerPose(m, now), s = m.spot;
    if (!p) continue;
    const [col, strength] = KINDS[s.kind].glow, { c, m: margin } = lightOf(s.w, s.h, col);
    glow.globalCompositeOperation = 'lighter';
    glow.globalAlpha = p.glow * strength;
    glow.drawImage(c, s.x - margin, s.y - margin);
    sprite(ink, NOTE, Math.round(s.x + s.w / 2) - 2, Math.max(1, s.y - 9), { x: p.wait ? '#cbbf9c' : '#fff3c4' }, p.wait ? 0.6 : 1);
    drawn++;
  }
  glow.globalAlpha = ink.globalAlpha = 1;
  glow.globalCompositeOperation = 'source-over';
  return drawn;
}

/** Draws the answers at `now` (ms) into `glow` and `ink` and drops the finished ones from `list`. Returns how many it drew. */
export function drawSpots(glow, ink, list, now) {
  let drawn = 0, keep = 0;
  for (const a of list) {
    const p = answerPose(a, now);
    if (!p && now >= a.born) continue;
    list[keep++] = a;
    if (!p) continue;
    const s = a.spot, { glow: [col, strength, round], show } = KINDS[s.kind];
    if (round) drawHalo(glow, roundOf(round, col), a.x, a.y, p.glow * strength);
    else {
      const { c, m } = lightOf(s.w, s.h, col);
      glow.globalCompositeOperation = 'lighter';
      glow.globalAlpha = p.glow * strength;
      glow.drawImage(c, s.x - m, s.y - m);
    }
    SHOWS[show](glow, ink, a, p, col);
    drawn++;
  }
  list.length = keep;
  glow.globalAlpha = ink.globalAlpha = 1;
  glow.globalCompositeOperation = 'source-over';
  return drawn;
}
